import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../utils/api';
import './Users.css';

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalType, setModalType] = useState(null);
  const limit = 20;

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const [usersData, countData] = await Promise.all([
        adminAPI.getUsers({ skip: page * limit, limit, search, plan: planFilter }),
        adminAPI.getUserCount({ search, plan: planFilter })
      ]);
      setUsers(usersData);
      setTotalCount(countData.count);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, search, planFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchUsers();
  };

  const openModal = (user, type) => {
    setSelectedUser(user);
    setModalType(type);
  };

  const closeModal = () => {
    setSelectedUser(null);
    setModalType(null);
  };

  const handleUpdateUser = async (userId, data) => {
    try {
      await adminAPI.updateUser(userId, data);
      fetchUsers();
      closeModal();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleResetPassword = async (userId, newPassword) => {
    try {
      await adminAPI.resetPassword(userId, newPassword);
      alert('Password reset successfully');
      closeModal();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleUpdateSubscription = async (userId, plan) => {
    try {
      await adminAPI.updateSubscription(userId, plan);
      fetchUsers();
      closeModal();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update subscription');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Are you sure you want to delete this user? This cannot be undone.')) {
      return;
    }
    try {
      await adminAPI.deleteUser(userId);
      fetchUsers();
      closeModal();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const totalPages = Math.ceil(totalCount / limit);

  return (
    <div className="users-page">
      <div className="page-header">
        <h1 className="page-title">Users</h1>
        <p className="page-subtitle">Manage user accounts and subscriptions</p>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-container">
            <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              className="search-input"
              placeholder="Search by email or name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button type="submit" className="btn btn-primary">Search</button>
        </form>

        <select
          className="form-input plan-filter"
          value={planFilter}
          onChange={(e) => { setPlanFilter(e.target.value); setPage(0); }}
        >
          <option value="">All Plans</option>
          <option value="free">Free</option>
          <option value="professional_go">Professional Go</option>
          <option value="professional_max">Professional Max</option>
          <option value="enterprise">Enterprise</option>
        </select>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
          <span>Loading users...</span>
        </div>
      ) : error ? (
        <div className="error-message">
          <p>{error}</p>
          <button className="btn btn-primary" onClick={fetchUsers}>Retry</button>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Plan</th>
                  <th>Usage</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="user-cell">
                        <div className="user-avatar">
                          {user.name?.[0]?.toUpperCase() || user.email[0].toUpperCase()}
                        </div>
                        <div className="user-info">
                          <div className="user-name">{user.name || 'No name'}</div>
                          <div className="user-email">{user.email}</div>
                        </div>
                        {user.is_admin && <span className="badge badge-info">Admin</span>}
                      </div>
                    </td>
                    <td>
                      <span className={`badge badge-${getPlanBadgeClass(user.subscription_plan)}`}>
                        {user.subscription_plan || 'None'}
                      </span>
                    </td>
                    <td>{user.current_month_usage} queries</td>
                    <td>
                      {user.phone_number ? (
                        <span className="badge badge-success" title={user.phone_number}>
                          {user.phone_number}
                        </span>
                      ) : (
                        <span className="badge badge-secondary">—</span>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${user.email_verified ? 'badge-success' : 'badge-warning'}`}>
                        {user.email_verified ? 'Verified' : 'Unverified'}
                      </span>
                    </td>
                    <td>{new Date(user.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => openModal(user, 'edit')}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => openModal(user, 'password')}
                        >
                          Reset PW
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteUser(user.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={() => setPage(p => p - 1)}
              disabled={page === 0}
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {page + 1} of {totalPages || 1}
            </span>
            <button
              className="pagination-btn"
              onClick={() => setPage(p => p + 1)}
              disabled={page >= totalPages - 1}
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* Edit Modal */}
      {selectedUser && modalType === 'edit' && (
        <EditUserModal
          user={selectedUser}
          onClose={closeModal}
          onSave={handleUpdateUser}
          onUpdateSubscription={handleUpdateSubscription}
        />
      )}

      {/* Password Reset Modal */}
      {selectedUser && modalType === 'password' && (
        <PasswordModal
          user={selectedUser}
          onClose={closeModal}
          onSave={handleResetPassword}
        />
      )}
    </div>
  );
}

function getPlanBadgeClass(plan) {
  switch (plan?.toLowerCase()) {
    case 'enterprise': return 'info';
    case 'professional_max': return 'success';
    case 'professional_go': return 'warning';
    case 'free': return 'secondary';
    default: return 'secondary';
  }
}

function EditUserModal({ user, onClose, onSave, onUpdateSubscription }) {
  const [name, setName] = useState(user.name || '');
  const [emailVerified, setEmailVerified] = useState(user.email_verified);
  const [isAdmin, setIsAdmin] = useState(user.is_admin);
  const [plan, setPlan] = useState(user.subscription_plan || 'free');
  const [secrets, setSecrets] = useState(null);
  const [secretsLoading, setSecretsLoading] = useState(false);
  const [showSecrets, setShowSecrets] = useState(false);

  const loadSecrets = async () => {
    if (secrets !== null) {
      setShowSecrets(!showSecrets);
      return;
    }
    setSecretsLoading(true);
    try {
      const data = await adminAPI.getUserSecrets(user.id);
      setSecrets(data.secrets || []);
      setShowSecrets(true);
    } catch {
      setSecrets([]);
      setShowSecrets(true);
    } finally {
      setSecretsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(user.id, { name, email_verified: emailVerified, is_admin: isAdmin });
  };

  const handlePlanChange = () => {
    onUpdateSubscription(user.id, plan);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Edit User</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Email</label>
              <input type="email" className="form-input" value={user.email} disabled />
            </div>
            <div className="form-group">
              <label className="form-label">Name</label>
              <input
                type="text"
                className="form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            {/* Phone Verification Status */}
            <div className="form-group">
              <label className="form-label">Phone</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="text"
                  className="form-input"
                  value={user.phone_number || 'Not linked'}
                  disabled
                  style={{ flex: 1 }}
                />
                {user.phone_number ? (
                  <span className="badge badge-success" style={{ whiteSpace: 'nowrap' }}>Linked</span>
                ) : (
                  <span className="badge badge-secondary" style={{ whiteSpace: 'nowrap' }}>Not linked</span>
                )}
              </div>
              {user.phone_channel && (
                <small style={{ color: '#6b7280', marginTop: '4px', display: 'block' }}>
                  Channel: {user.phone_channel}
                </small>
              )}
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={emailVerified}
                  onChange={(e) => setEmailVerified(e.target.checked)}
                />
                <span>Email Verified</span>
              </label>
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={isAdmin}
                  onChange={(e) => setIsAdmin(e.target.checked)}
                />
                <span>Admin User</span>
              </label>
            </div>

            <hr className="modal-divider" />

            <div className="form-group">
              <label className="form-label">Subscription Plan</label>
              <div className="plan-change-row">
                <select
                  className="form-input"
                  value={plan}
                  onChange={(e) => setPlan(e.target.value)}
                >
                  <option value="free">Free</option>
                  <option value="professional_go">Professional Go</option>
                  <option value="professional_max">Professional Max</option>
                  <option value="enterprise">Enterprise</option>
                </select>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handlePlanChange}
                >
                  Update Plan
                </button>
              </div>
            </div>

            <hr className="modal-divider" />

            {/* Secrets Vault */}
            <div className="form-group">
              <label className="form-label">Secrets Vault</label>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={loadSecrets}
                disabled={secretsLoading}
              >
                {secretsLoading ? 'Loading...' : showSecrets ? 'Hide Secrets' : 'Show Stored Keys'}
              </button>
              {showSecrets && secrets && (
                <div style={{ marginTop: '8px' }}>
                  {secrets.length === 0 ? (
                    <small style={{ color: '#6b7280' }}>No secrets stored</small>
                  ) : (
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px' }}>
                      {secrets.map((s, i) => (
                        <li key={i} style={{ marginBottom: '4px' }}>
                          <code>{s.key_name}</code>
                          {s.description && <span style={{ color: '#6b7280' }}> — {s.description}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PasswordModal({ user, onClose, onSave }) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    onSave(user.id, password);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Reset Password</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <p className="modal-info">Resetting password for: <strong>{user.email}</strong></p>

            {error && <div className="form-error">{error}</div>}

            <div className="form-group">
              <label className="form-label">New Password</label>
              <input
                type="password"
                className="form-input"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                placeholder="Enter new password"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <input
                type="password"
                className="form-input"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                placeholder="Confirm new password"
                required
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Reset Password
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Users;
